import type { ChangeEvent } from 'react';
import {
  REVIEW_MEDIA_MAX_FILE_SIZE_BYTES,
  REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES,
  REVIEW_VIDEO_MAX_DURATION_SECONDS,
  isReviewMediaContentType,
  isReviewVideoContentType,
} from '../../model/review/review';
import {
  REVIEW_VIDEO_DURATION_ERROR_MESSAGE,
  REVIEW_VIDEO_SIZE_ERROR_MESSAGE,
  reviewMediaSizeErrorMessage,
  reviewMediaTypeErrorMessage,
} from '../../infrastructure/review/utils/mediaUpload';

/**
 * Shared utility for generating unique review image IDs (used by both webapp and mobile)
 */
export const generateReviewImageId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export interface ReviewImageFile extends File {
  id: string;
}

export const createReviewImageFile = (file: File): ReviewImageFile => {
  const reviewImageFile = file as ReviewImageFile;
  reviewImageFile.id = generateReviewImageId();
  return reviewImageFile;
};

const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

/**
 * Apple devices hand back HEIC/HEIF, which browsers other than Safari cannot decode — the review
 * image renders as a broken thumbnail. Detection has to survive two quirks:
 *   - browsers frequently report an empty `type` for a `.heic` file, so the extension is the fallback
 *   - the file may be named `.jpg` while the bytes are HEIC, so the extension alone is not enough
 */
export const isHeicFile = (file: Pick<File, 'name' | 'type'>): boolean => {
  if (HEIC_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }

  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((extension) => name.endsWith(extension));
};

/**
 * Decodes HEIC/HEIF bytes and re-encodes them as JPEG. Injected rather than imported so this module
 * stays dependency-free and usable from React Native, where the browser wasm decoder cannot load.
 */
export type HeicToJpegConverter = (file: File) => Promise<Blob>;

const JPEG_MIME_TYPE = 'image/jpeg';

const toJpegFileName = (fileName: string): string => fileName.replace(/(\.[^.]*)?$/, '.jpg');

/**
 * Guarantees an uploadable file: HEIC/HEIF is transcoded to JPEG, everything else passes through
 * untouched. A converter failure is intentionally propagated so the caller blocks the upload rather
 * than storing bytes no browser can render.
 */
export const normalizeReviewImageFile = async (file: File, convertHeicToJpeg: HeicToJpegConverter): Promise<File> => {
  if (!isHeicFile(file)) {
    return file;
  }

  const jpegBlob = await convertHeicToJpeg(file);

  return new File([jpegBlob], toJpegFileName(file.name), {
    type: JPEG_MIME_TYPE,
    lastModified: file.lastModified,
  });
};

export interface ReviewImagePreview {
  id: string;
  url: string;
  /** A video needs a `<video>` element; an `<img>` renders it as a broken thumbnail. */
  isVideo: boolean;
}

export const createImagePreviews = (images: ReviewImageFile[]): ReviewImagePreview[] => {
  return images.map((file) => ({
    id: file.id,
    url: URL.createObjectURL(file),
    isVideo: file.type.startsWith('video/'),
  }));
};

/** Injected like the HEIC converter: the browser reads it off a `<video>`, RN off its media library. */
export type VideoDurationReader = (file: File) => Promise<number>;

/** Injected per platform; returning the file it was given means no encode was needed. */
export type ReviewVideoEncoder = (file: File) => Promise<File>;

export interface HandleImageUploadOptions {
  convertHeicToJpeg: HeicToJpegConverter;
  onError?: (message: string) => void;
  maxImages?: number;
  readVideoDuration?: VideoDurationReader;
  encodeVideo?: ReviewVideoEncoder;
}

export const HEIC_CONVERSION_ERROR_MESSAGE =
  "We couldn't read that photo. Please try a different one, or save it as JPEG and upload again.";

export const handleImageUpload = async (
  e: ChangeEvent<HTMLInputElement>,
  currentImages: ReviewImageFile[],
  setImages: (images: ReviewImageFile[]) => void,
  { convertHeicToJpeg, onError, maxImages = 5, readVideoDuration, encodeVideo }: HandleImageUploadOptions,
) => {
  const input = e.target;
  if (!input.files) {
    return;
  }

  // Read the FileList before awaiting: clearing the input below detaches it.
  const pickedFiles = Array.from(input.files);

  // Drop anything past the cap *before* converting. A file input cannot limit how many files are
  // picked, so a visitor with four photos already attached can select twenty more; decoding HEIC
  // costs a wasm run and a full-resolution bitmap per photo, and any failure among the surplus
  // would surface an error for a photo that was never going to be uploaded.
  const remainingSlots = Math.max(0, maxImages - currentImages.length);
  const filesToProcess = pickedFiles.slice(0, remainingSlots);

  if (filesToProcess.length === 0) {
    // Nothing can be added, so leave the stored images untouched rather than writing an
    // identical array — a new array identity would rebuild every preview blob URL.
    input.value = '';
    return;
  }

  const normalized = await Promise.all(
    filesToProcess.map(async (file) => {
      try {
        const normalizedFile = await normalizeReviewImageFile(file, convertHeicToJpeg);
        // Here rather than at submit: learning it after writing the review loses the work.
        if (!isReviewMediaContentType(normalizedFile.type)) {
          onError?.(reviewMediaTypeErrorMessage(normalizedFile.name));
          return null;
        }

        if (!isReviewVideoContentType(normalizedFile.type)) {
          if (normalizedFile.size > REVIEW_MEDIA_MAX_FILE_SIZE_BYTES) {
            onError?.(reviewMediaSizeErrorMessage(normalizedFile.name));
            return null;
          }
          return normalizedFile;
        }

        // Gated before the encode: refusing afterwards wastes the reviewer's time.
        if (readVideoDuration) {
          const durationSeconds = await readVideoDuration(normalizedFile).catch(() => null);
          // Unreadable is not a rejection — the size ceiling still bounds what goes up.
          if (durationSeconds !== null && durationSeconds > REVIEW_VIDEO_MAX_DURATION_SECONDS) {
            onError?.(REVIEW_VIDEO_DURATION_ERROR_MESSAGE);
            return null;
          }
        }

        const encodedFile = encodeVideo ? await encodeVideo(normalizedFile).catch(() => normalizedFile) : normalizedFile;
        const wasEncoded = encodedFile !== normalizedFile;
        // The encode aims under its target but overshoots, so it answers to the server's ceiling
        // rather than the lower number a reviewer is shown for a file we did not produce.
        const sizeCeiling = wasEncoded ? REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES : REVIEW_MEDIA_MAX_FILE_SIZE_BYTES;

        if (encodedFile.size > sizeCeiling) {
          onError?.(REVIEW_VIDEO_SIZE_ERROR_MESSAGE);
          return null;
        }

        return encodedFile;
      } catch {
        // One unreadable photo must not discard the rest of the selection.
        onError?.(HEIC_CONVERSION_ERROR_MESSAGE);
        return null;
      }
    }),
  );

  const fileArray = normalized.filter((file): file is File => file !== null).map(createReviewImageFile);

  if (fileArray.length === 0) {
    // Left alone rather than rewritten: an equal array rebuilds a preview blob per attachment.
    input.value = '';
    return;
  }

  const newImages = [...currentImages, ...fileArray];

  const limitedImages = newImages.slice(0, maxImages);
  setImages(limitedImages);
  input.value = '';
};

export const removeImageById = (
  id: string,
  currentImages: ReviewImageFile[],
  setImages: (images: ReviewImageFile[]) => void,
) => {
  const newImages = currentImages.filter((img) => img.id !== id);
  setImages(newImages);
};

/** @deprecated Use removeImageById instead for proper React key handling */
export const removeImage = (index: number, currentImages: File[], setImages: (images: File[]) => void) => {
  const newImages = currentImages.filter((_, i) => i !== index);
  setImages(newImages);
};

export const cleanupImagePreviews = (previews: string[]) => {
  previews.forEach((url) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

export const validateImageFiles = (files: FileList | null): { valid: boolean; errors: string[] } => {
  if (!files) {
    return { valid: true, errors: [] };
  }

  const errors: string[] = [];
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE_10MB = 10 * 1024 * 1024;

  Array.from(files).forEach((file, index) => {
    if (!validTypes.includes(file.type)) {
      errors.push(`File ${index + 1}: Only JPEG, PNG, and WebP images are allowed`);
    }

    if (file.size > MAX_FILE_SIZE_10MB) {
      errors.push(`File ${index + 1}: File size must be less than 10MB`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
